import { IndianRupee, Truck, Warehouse, Package, Weight, Clock, MapPin } from "lucide-react";

const ZONE_COLORS = {
  A: "bg-emerald-50 text-emerald-700 border-emerald-200",
  B: "bg-blue-50 text-blue-700 border-blue-200",
  C: "bg-amber-50 text-amber-700 border-amber-200",
  D: "bg-red-50 text-red-700 border-red-200",
};

function HubZoneBadge({ zone, zoneLabel }) {
  if (!zone) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-bold ${ZONE_COLORS[zone] || ZONE_COLORS.A}`}
      data-testid="hub-zone-badge"
    >
      <MapPin className="h-3 w-3" />
      Zone {zone}
      {zoneLabel && <span className="font-semibold opacity-80">· {zoneLabel}</span>}
    </span>
  );
}

export default function HubPriceCard({ pricing, compact = false, isAdmin = false }) {
  if (!pricing || !pricing.stages) return null;

  if (compact) {
    return (
      <div className="inline-flex flex-col gap-0.5" data-testid="hub-price-inline">
        <span className="text-sm font-bold text-slate-900 inline-flex items-center gap-0.5">
          <IndianRupee className="h-3.5 w-3.5" />{pricing.final_price}
          {pricing.lazy_discount > 0 && (
            <span className="text-[10px] text-green-600 font-medium ml-1">-{pricing.lazy_discount}</span>
          )}
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {pricing.zone && <HubZoneBadge zone={pricing.zone} zoneLabel={pricing.zone_label} />}
          <span className="text-[10px] text-violet-600 font-medium inline-flex items-center gap-1">
            <Warehouse className="h-2.5 w-2.5" /> Hub Routed
            {pricing.delivery_speed === "lazy" && <span className="text-purple-600 ml-1">Lazy</span>}
            {pricing.batch_size > 1 && <span className="text-green-600 ml-1">Batch x{pricing.batch_size}</span>}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-violet-50/80 via-indigo-50/40 to-white border border-violet-200/80 rounded-xl p-4" data-testid="hub-price-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center shadow-sm shadow-violet-600/20">
            <Warehouse className="h-4 w-4 text-white" />
          </div>
          <div>
            <span className="text-sm font-semibold text-slate-800">{isAdmin ? "Hub Route Pricing" : "Delivery Pricing"}</span>
            <p className="text-[10px] text-slate-400">{pricing.total_distance_km} km total</p>
          </div>
        </div>
        {isAdmin && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
            pricing.pricing_mode === "flat" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"
          }`}>
            {pricing.pricing_mode === "flat" ? pricing.zone_info?.label : "Dynamic"}
          </span>
        )}
      </div>

      {/* Zone & Distance Info — surfaces WHY the price scaled with distance */}
      {(pricing.zone || (pricing.total_distance_km ?? pricing.distance_km) > 0) && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {pricing.zone && <HubZoneBadge zone={pricing.zone} zoneLabel={pricing.zone_label} />}
          {(pricing.total_distance_km ?? pricing.distance_km) > 0 && (
            <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg text-xs font-medium" data-testid="hub-distance-badge">
              <span className="text-[9px] font-bold text-violet-600 bg-violet-50 px-1 py-0.5 rounded mr-0.5">ROAD</span>
              {pricing.total_distance_km ?? pricing.distance_km} km
            </span>
          )}
          <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 border border-violet-200 px-2.5 py-1 rounded-lg text-xs font-bold">
            <Warehouse className="h-3 w-3" />
            Hub Routed
          </span>
        </div>
      )}

      {/* Admin: Stage Breakdown */}
      {/* REMOVED for production: legacy 5-stage cost rows used to render here when isAdmin.
          They were the "internal hub formula" total (~₹1495 for the screenshot order)
          but the actual billed TOTAL comes from the unified slab engine (~₹2122).
          The mismatch confused admins. Admin now sees the SAME customer billing
          breakdown below, identical to what the customer & agent see.
          The legacy hub_pricing data is still on the order document for future
          internal cost/payout analysis — see hub_pricing.stages on the API response. */}

      {/* Unified billing breakdown — shown to ALL roles (customer / admin / agent). */}
      <div className="space-y-2 text-xs" data-testid="hub-customer-breakdown">
          {(pricing.pickup_packing_charge ?? 0) > 0 && (
            <div className="flex justify-between text-slate-500" data-testid="service-charges-line">
              <span className="flex items-center gap-1.5">
                <Package className="h-3 w-3 text-slate-400" />
                Service Charges <span className="text-[10px] text-slate-400">(Pickup + Packing)</span>
              </span>
              <span className="font-medium text-slate-700">INR {pricing.pickup_packing_charge}</span>
            </div>
          )}
          {(pricing.weight_charge_display ?? 0) > 0 && (
            <div className="flex justify-between text-slate-500" data-testid="weight-line">
              <span className="flex items-center gap-1.5">
                <Weight className="h-3 w-3 text-slate-400" />
                Weight Charges <span className="text-[10px] text-slate-400">(Slab: {pricing.weight_slab || `${pricing.chargeable_weight_kg ?? pricing.weight_kg} kg`})</span>
              </span>
              <span className="font-medium text-slate-700">INR {pricing.weight_charge_display}</span>
            </div>
          )}
          {/* Chargeable weight breakdown — hidden from customer per Sprint 27.1.
              Internal logic still computes actual/volumetric/chargeable kg
              and feeds the slab pick — customer just doesn't see the
              breakdown. */}
          {(pricing.delivery_charge ?? 0) > 0 && (
            <div className="flex justify-between text-slate-500" data-testid="delivery-line">
              <span className="flex items-center gap-1.5">
                <Truck className="h-3 w-3 text-slate-400" />
                Service Charges
              </span>
              <span className="font-medium text-slate-700">INR {pricing.delivery_charge}</span>
            </div>
          )}
          {(pricing.longhaul_surcharge ?? 0) > 0 && (
            <div className="flex justify-between text-slate-500" data-testid="longhaul-surcharge-line">
              <span className="flex items-center gap-1.5">
                <Truck className="h-3 w-3 text-amber-500" />
                Long-haul Surcharge{" "}
                <span className="text-[10px] text-slate-400">
                  ({Math.max(0, (pricing.chargeable_weight_kg ?? pricing.weight_kg ?? 0) - (pricing.longhaul_surcharge_free_kg ?? 0)).toFixed(1)} kg × {pricing.total_distance_km ?? pricing.distance_km} km × ₹{pricing.longhaul_surcharge_per_kgkm})
                </span>
              </span>
              <span className="font-medium text-slate-700">INR {pricing.longhaul_surcharge}</span>
            </div>
          )}
          {(pricing.customer_gst ?? pricing.gst) > 0 && (
            <div className="flex justify-between text-slate-500" data-testid="gst-line">
              <span className="flex items-center gap-1">
                <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded">GST</span>
                Tax ({pricing.gst_rate_pct ?? 18}%)
              </span>
              <span className="font-medium text-slate-700">INR {pricing.customer_gst ?? pricing.gst}</span>
            </div>
          )}
          {(pricing.cod_fee ?? 0) > 0 && (
            <div className="flex justify-between text-amber-700" data-testid="cod-fee-line">
              <span className="flex items-center gap-1.5">
                <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1 py-0.5 rounded">COD</span>
                Cash-on-Delivery Fee ({pricing.cod_fee_pct ?? 5}%)
              </span>
              <span className="font-medium text-amber-700">INR {pricing.cod_fee}</span>
            </div>
          )}
        </div>

      {/* REMOVED for production: legacy admin-only Packing / Weight / Operational Cost / Platform Fee rows.
          These reflected the internal 5-stage hub formula and did not reconcile with the
          billed TOTAL (which comes from the unified slab engine). The slab-based
          Weight Charges row above is now the single source displayed to admin. */}

      {/* Admin only: Flat Price Notice */}
      {isAdmin && pricing.pricing_mode === "flat" && pricing.zone_info?.applies && (
        <div className="mt-2 px-3 py-1.5 bg-emerald-50/60 border border-emerald-100 rounded-lg">
          <p className="text-[10px] text-emerald-700 font-medium">
            {pricing.zone_info.label} flat rate applied — INR {pricing.zone_info.flat_price}
          </p>
        </div>
      )}

      {/* Admin only: Profit Warning */}
      {isAdmin && pricing.profit_warning && (
        <div className="mt-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-[10px] text-red-700 font-bold">Below-cost pricing detected</p>
        </div>
      )}

      {/* Lazy Delivery Discount */}
      {pricing.lazy_discount > 0 && (
        <div className="mt-2 px-3 py-1.5 bg-purple-50/60 border border-purple-200 rounded-lg flex justify-between items-center">
          <span className="text-[10px] text-purple-700 font-bold flex items-center gap-1">
            <Clock className="h-3 w-3" /> Lazy Delivery ({pricing.lazy_discount_pct ?? 15}% off)
          </span>
          <span className="text-xs font-bold text-green-600">-INR {pricing.lazy_discount}</span>
        </div>
      )}

      {/* Final Total */}
      <div className="border-t border-violet-200/60 pt-3 mt-2 flex justify-between items-center">
        <span className="font-bold text-slate-900 text-sm tracking-tight">TOTAL</span>
        <span className="font-extrabold text-violet-700 text-lg tracking-tight" data-testid="hub-final-price">
          INR {pricing.final_price}
        </span>
      </div>

      {/* Admin Pickup/Delivery Agent payouts removed (Feb 2026) — internal-only data, not relevant on the booking quote. */}
    </div>
  );
}
